
/*
  Purpose: Show product-specific offers between cart products and notes
           in Impulse's cart drawer.
  Dependencies: sections/cart-upsell.liquid;
                #CartDrawer .drawer__inner .drawer__scrollable.
*/
(() => {
  const drawerSelector = '#CartDrawer';
  const hostClass = 'bdt-upsell-host';
  const root = window.Shopify?.routes?.root || '/';

  let timer;
  let requestNumber = 0;
  let drawerObserver;

  function getDrawer() {
    return document.querySelector(drawerSelector);
  }

  function mount() {
    // Use the populated cart's scrollable area, not the empty-cart area.
    const scrollable = getDrawer()?.querySelector(
      '.drawer__inner .drawer__scrollable'
    );
    if (!scrollable) return null;

    const notesRow = scrollable.querySelector('#CartNoteDrawer')?.parentElement;
    const products = scrollable.querySelector('[data-products]');
    let host = scrollable.querySelector(`.${hostClass}`);
    const isNew = !host;

    if (isNew) {
      host = document.createElement('div');
      host.className = hostClass;
    }

    if (notesRow?.parentElement === scrollable) {
      // Correct order: products, upsell, notes.
      if (host.parentElement !== scrollable ||
          host.nextElementSibling !== notesRow) {
        scrollable.insertBefore(host, notesRow);
      }
    } else if (products?.parentElement === scrollable) {
      // If order notes are disabled, put the upsell after the products.
      if (host.parentElement !== scrollable ||
          host.previousElementSibling !== products) {
        products.insertAdjacentElement('afterend', host);
      }
    } else if (host.parentElement !== scrollable) {
      scrollable.appendChild(host);
    }

    if (isNew) scheduleRefresh();
    return host;
  }

  function watchDrawer() {
    const drawer = getDrawer();
    if (!drawer || drawerObserver) return;

    drawerObserver = new MutationObserver(() => {
      // Impulse may replace drawer contents after a cart change.
      mount();
    });

    drawerObserver.observe(drawer, { childList: true, subtree: true });
    mount();
  }

  function scheduleRefresh() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 150);
  }

  async function refresh() {
    const host = mount();
    if (!host) return;

    const thisRequest = ++requestNumber;

    try {
      const response = await fetch(`${root}cart?section_id=cart-upsell`, {
        credentials: 'same-origin',
        cache: 'no-store'
      });

      if (!response.ok) throw new Error('Upsell section could not load');

      const html = await response.text();
      if (thisRequest !== requestNumber) return;

      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const nextSection = parsed.querySelector('.bdt-upsell');
      const currentHost = getDrawer()?.querySelector(
        `.drawer__inner .${hostClass}`
      );

      if (currentHost) {
        currentHost.replaceChildren();
        if (nextSection) currentHost.appendChild(nextSection);
      }
    } catch (error) {
      // An upsell failure must never interrupt the customer's cart.
      console.warn('Bonza cart upsell:', error);
    }
  }

  document.addEventListener('cart:updated', scheduleRefresh);
  document.addEventListener('ajaxProduct:added', scheduleRefresh);

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-bdt-upsell-add]');
    if (!button) return;

    const errorElement = button
      .closest('.bdt-upsell__details')
      ?.querySelector('.bdt-upsell__error');

    button.disabled = true;
    button.textContent = 'Adding…';
    if (errorElement) errorElement.hidden = true;

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          items: [{
  id: Number(button.dataset.variantId),
  quantity: Number(button.dataset.quantity) || 1,
  properties: {
    _bonza_upsell: 'drawer'
  }
}]
        })
      });

      if (!response.ok) {
        const result = await response.json();
        throw new Error(result.description || 'Please try again.');
      }

      // Ask Impulse to redraw its own cart items, totals and count.
      document.dispatchEvent(new CustomEvent('cart:build'));
      scheduleRefresh();
    } catch (error) {
      button.disabled = false;
      button.textContent = 'Add to cart';

      if (errorElement) {
        errorElement.textContent = error.message;
        errorElement.hidden = false;
      }
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchDrawer, { once: true });
  } else {
    watchDrawer();
  }
})();
